/**
 * The request log, on screen.
 *
 * Deliberately thin: everything worth arguing about - what an empty table
 * means, whether a number is rows or requests, what a sampled row stands
 * for - lives in lib/logs.ts where a test holds it.
 *
 * Newest FIRST here, unlike the CLI. In a terminal the newest line should
 * land next to the prompt; on a page the eye starts at the top.
 */

import { useCallback, useEffect, useState } from 'react'
import { useProject } from '../components/ProjectLayout.js'
import { ErrorBox, PageHeader } from '../components/ui.js'
import { useApi } from '../lib/useApi.js'
import type { LogEntry, LogPage } from '../lib/api.js'
import {
  LEVELS, PAGE_LIMIT, ROUTES, WINDOWS, at, duration, emptyState, hasDetail, isSampled,
  levelClass, summarise, truncationNote,
  type LogLevel, type LogWindow,
} from '../lib/logs.js'

export default function Logs() {
  const { project } = useProject()
  const api = useApi()
  const [window_, setWindow] = useState<LogWindow>('1h')
  const [level, setLevel] = useState<LogLevel | ''>('')
  const [route, setRoute] = useState('')
  const [page, setPage] = useState<LogPage | null>(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const load = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      setPage(await api.projectLogs(project.id, {
        since: window_, ...(level ? { level } : {}), ...(route ? { route } : {}),
        limit: PAGE_LIMIT,
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the log.')
    } finally {
      setBusy(false)
    }
  }, [api, project.id, window_, level, route])

  useEffect(() => { void load() }, [load])

  const entries = page?.entries ?? []
  const empty = emptyState({
    ...(page?.unavailable ? { unavailable: page.unavailable } : {}),
    window: window_,
    filtered: level !== '',
  })

  return (
    <>
      <PageHeader
        title="Logs"
        description="Recent requests to this project: what was called, what it answered, and why it failed."
      />

      <section className="card panel">
        <div className="row">
          <label>
            <span className="sub">Time</span>
            <select value={window_} onChange={(e) => setWindow(e.target.value as LogWindow)}>
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </label>
          <label>
            <span className="sub">Level</span>
            <select value={level} onChange={(e) => setLevel(e.target.value as LogLevel | '')}>
              <option value="">All</option>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>
            <span className="sub">Route</span>
            <select value={route} onChange={(e) => setRoute(e.target.value)}>
              <option value="">All routes</option>
              {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button className="btn" onClick={() => void load()} disabled={busy}>
            {busy ? 'Reading…' : 'Refresh'}
          </button>
        </div>
        {entries.length > 0 && <p className="sub">{summarise(entries)}</p>}
        {truncationNote(entries) && (
          <p className="sub small">{truncationNote(entries)}</p>
        )}
        {isSampled(entries) && (
          // Said where the numbers are, not in a footnote. A count that
          // silently under-reports busy periods is worse than one that
          // explains itself.
          <p className="sub">
            Some of these lines are sampled: each stands for several requests, so the totals above
            are an estimate rather than an exact count.
          </p>
        )}
      </section>

      {error && <ErrorBox>{error}</ErrorBox>}

      {!busy && entries.length === 0 && (
        <section className={`card panel ${empty.severity === 'problem' ? 'panel-warn' : ''}`} aria-live="polite">
          <h2>{empty.title}</h2>
          <p className="sub">{empty.detail}</p>
        </section>
      )}

      {entries.length > 0 && (
        <section className="card panel">
          <table className="table">
            <thead>
              <tr>
                <th>Time</th><th>Level</th><th>Request</th><th>Status</th><th>Took</th><th>Rows</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <Row
                  key={`${entry.timestamp}-${i}`}
                  entry={entry}
                  open={open === i}
                  onToggle={() => setOpen(open === i ? null : i)}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  )
}

function Row({ entry, open, onToggle }: { entry: LogEntry; open: boolean; onToggle: () => void }) {
  const expandable = hasDetail(entry)
  return (
    <>
      <tr
        onClick={expandable ? onToggle : undefined}
        style={expandable ? { cursor: 'pointer' } : undefined}
        aria-expanded={expandable ? open : undefined}
      >
        <td>{at(entry.timestamp)}</td>
        <td><span className={levelClass(entry.level)}>{entry.level}</span></td>
        <td>
          <code>{entry.method} {entry.route}</code>
          {entry.sampleInterval > 1 && (
            <span className="pill" title={`Stands for about ${entry.sampleInterval} requests`}>
              ×{entry.sampleInterval}
            </span>
          )}
        </td>
        <td>{entry.status}</td>
        <td>{duration(entry.durationMs)}</td>
        <td className="sub">{entry.rowsRead}r / {entry.rowsWritten}w</td>
      </tr>
      {expandable && open && (
        <tr>
          <td colSpan={6}>
            {entry.message && <p>{entry.message}</p>}
            {/* The statement gets its own block: when it is there, it is the
                only thing anybody wants to read. */}
            {entry.statement && <pre className="code">{entry.statement}</pre>}
            <p className="sub">outcome: {entry.outcome} · credential: {entry.credential}</p>
          </td>
        </tr>
      )}
    </>
  )
}
